"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { AnimatePresence, motion } from "motion/react";
import { Link } from "@/i18n/navigation";
import { Switch } from "@/components/ui/switch";
import useCookieConsent from "@/hooks/useCookieConsent";
import {
    CONSENT_OPEN_EVENT,
    applyConsentScripts,
    clearMarketingCookies,
    readConsent,
    syncPostHogConsent,
    writeConsent,
} from "@/lib/cookieConsent";

/**
 * Carte de consentement aux cookies, en bas à gauche de l'écran.
 *
 * Elle se montre tant que le visiteur n'a rien choisi, et se rouvre depuis le
 * lien « Gérer les cookies » du pied de page. « Tout refuser » et « Tout
 * accepter » ont la même taille et la même place ; seul « Tout accepter » est
 * en noir. Elle ne bloque rien : le site reste utilisable, et sans choix, rien n'est déposé.
 *
 * C'est aussi elle qui applique le choix à chaque chargement : scripts tiers
 * (lib/cookieConsent.js) et PostHog.
 */
export default function CookieConsent() {
    const t = useTranslations("layout");
    const posthog = usePostHog();
    const consent = useCookieConsent();

    // Ouverte d'office tant que le visiteur n'a rien choisi, ou rouverte
    // depuis « Gérer les cookies ».
    const [reopened, setReopened] = useState(false);
    const isOpen = consent === null || reopened;
    const [showDetails, setShowDetails] = useState(false);
    const [choice, setChoice] = useState({
        analytics: false,
        marketing: false,
    });

    // Applique le choix enregistré à chaque chargement et à chaque changement.
    useEffect(() => {
        if (consent === undefined) return;
        syncPostHogConsent(posthog, consent);
        applyConsentScripts(consent);
    }, [consent, posthog]);

    // « Gérer les cookies » : rouvre la carte sur le détail des finalités.
    useEffect(() => {
        const onOpen = () => {
            const current = readConsent();
            setChoice({
                analytics: current?.analytics === true,
                marketing: current?.marketing === true,
            });
            setShowDetails(true);
            setReopened(true);
        };
        window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
        return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
    }, []);

    const save = (next) => {
        const previous = readConsent();
        writeConsent(next);
        setReopened(false);
        setShowDetails(false);

        // Un script déjà chargé ne se décharge pas : retirer un accord donné
        // efface ce qu'on peut, puis recharge la page pour tout arrêter.
        const revokedAnalytics = previous?.analytics && !next.analytics;
        const revokedMarketing = previous?.marketing && !next.marketing;
        if (revokedAnalytics || revokedMarketing) {
            syncPostHogConsent(posthog, readConsent());
            if (revokedMarketing) clearMarketingCookies();
            window.location.reload();
        }
    };

    const buttonClass =
        "rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-200 dark:bg-bgDark-700 dark:text-white dark:hover:bg-bgDark-600";

    const acceptButtonClass =
        "rounded-xl bg-black px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200";

    const purposes = [
        { key: "analytics", title: "Consent_analytics_title", text: "Consent_analytics_text" },
        { key: "marketing", title: "Consent_marketing_title", text: "Consent_marketing_text" },
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.section
                    role="region"
                    aria-label={t("Consent_region_label")}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="fixed inset-x-4 bottom-4 z-[60] rounded-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-borderDark dark:bg-bgDark-800 sm:inset-x-auto sm:bottom-6 sm:left-6 sm:w-[380px]"
                >
                    <p className="mb-1.5 text-sm font-bold text-gray-900 dark:text-white">
                        {t("Consent_title")}
                    </p>
                    <p className="mb-4 text-sm leading-relaxed text-gray-600 dark:text-grayDark-300">
                        {t("Consent_text")}{" "}
                        <Link
                            href="/privacy"
                            className="font-medium text-gray-900 underline underline-offset-2 dark:text-white"
                        >
                            {t("Consent_learnMore")}
                        </Link>
                    </p>

                    {showDetails && (
                        <div className="mb-4 space-y-3 border-t border-gray-100 pt-4 dark:border-borderDark">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="mb-0.5 text-sm font-semibold text-gray-900 dark:text-white">
                                        {t("Consent_necessary_title")}
                                    </p>
                                    <p className="mb-0 text-xs leading-relaxed text-gray-500 dark:text-grayDark-400">
                                        {t("Consent_necessary_text")}
                                    </p>
                                </div>
                                <span className="shrink-0 pt-0.5 text-xs font-medium text-gray-400 dark:text-grayDark-400">
                                    {t("Consent_alwaysOn")}
                                </span>
                            </div>
                            {purposes.map((purpose) => (
                                <label
                                    key={purpose.key}
                                    className="flex cursor-pointer items-start justify-between gap-4"
                                >
                                    <span>
                                        <span className="mb-0.5 block text-sm font-semibold text-gray-900 dark:text-white">
                                            {t(purpose.title)}
                                        </span>
                                        <span className="block text-xs leading-relaxed text-gray-500 dark:text-grayDark-400">
                                            {t(purpose.text)}
                                        </span>
                                    </span>
                                    <Switch
                                        className="mt-0.5"
                                        checked={choice[purpose.key]}
                                        onCheckedChange={(checked) =>
                                            setChoice((prev) => ({
                                                ...prev,
                                                [purpose.key]: checked,
                                            }))
                                        }
                                    />
                                </label>
                            ))}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            className={buttonClass}
                            onClick={() =>
                                save({ analytics: false, marketing: false })
                            }
                        >
                            {t("Consent_refuseAll")}
                        </button>
                        {showDetails ? (
                            <button
                                type="button"
                                className={buttonClass}
                                onClick={() => save(choice)}
                            >
                                {t("Consent_save")}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={acceptButtonClass}
                                onClick={() =>
                                    save({ analytics: true, marketing: true })
                                }
                            >
                                {t("Consent_acceptAll")}
                            </button>
                        )}
                    </div>

                    {!showDetails && (
                        <button
                            type="button"
                            onClick={() => setShowDetails(true)}
                            className="mt-3 w-full text-center text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:text-grayDark-400 dark:hover:text-grayDark-200"
                        >
                            {t("Consent_customize")}
                        </button>
                    )}
                </motion.section>
            )}
        </AnimatePresence>
    );
}
